import { EvidencePdfDownload } from '@/components/evidence-viewer/EvidencePdfDownload'
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ListChecks,
  Download,
  Eye,
  ExternalLink,
  Database,
  Link2,
  Unlink,
  Trash2,
  Loader2,
  Cloud,
  Film,
  Globe,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  Package,
  Folder,
  Upload,
  TerminalSquare,
} from 'lucide-react'
import { toast } from 'sonner'
import { trpc, trpcClient } from '@/lib/trpc'
import { saveZipResult } from '@/components/auditor-center/evidence-download'
import { cn } from '@/lib/utils'
import { restoreSnapshotFileMetadata, type SubmissionArtifactSnapshot } from './submission-artifact-snapshot'
import type { RecommendedEvidencePlacement } from './RecommendedEvidenceSection'
import {
  folderOfBundleFile,
  partitionRunbookBundleFiles,
} from './runbook-bundle-tree'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@deepguide-ai/ui/alert-dialog'
import { Badge } from '@deepguide-ai/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { EvidenceFilePreviewDialog } from '@/components/auditor-center/EvidenceFilePreviewDialog'
import { getFileType } from '@/components/evidence-viewer'
import { FileExtensionIcon } from '@/utils/file-icons'
import { formatFileSize } from '@/utils/format-file-size'
import { PolicyContentViewer } from '@/components/screenata/PolicyContentViewer'
import type {
  TestType,
  RunbookArtifact,
  CollectorSourceCardData,
} from '../types'

/**
 * Empty state for an automated check that HAS run history. Its proof is the
 * run record, not an upload, so the tall "drag & drop a file" dropzone would
 * state the opposite of the truth. Attaching stays available for
 * supplementary evidence. Sits inside the card chrome like the plain-text
 * placeholder, so it must not supply its own border.
 */
function AutomatedCheckEmptyState({
  runCount,
  onAttachFiles,
}: {
  runCount: number
  onAttachFiles?: () => void
}) {
  return (
    <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3'>
      <div className='flex min-w-0 items-center gap-2.5'>
        <TerminalSquare
          className='h-3.5 w-3.5 shrink-0 text-text-subtle'
          aria-hidden
        />
        <p className='min-w-0 text-[12px] leading-snug text-text-body'>
          Evidence for this test is produced by its automated check:{' '}
          {runCount} run{runCount === 1 ? '' : 's'} recorded. See the run
          history below.
        </p>
      </div>
      {onAttachFiles && (
        <button
          type='button'
          onClick={onAttachFiles}
          className='shrink-0 cursor-pointer text-[12px] font-medium text-cobalt-strong underline-offset-2 hover:underline'
        >
          Attach a file
        </button>
      )}
    </div>
  )
}

export interface EvidenceBulkAction {
  onClick: () => void
  disabled: boolean
}

export interface ArtifactsSectionProps {
  /** Replaces "No evidence yet" when the evidence lives elsewhere (a sampling round's submission). */
  emptyTitle?: string
  testType: TestType
  filesData: unknown[] | undefined
  latestRunbookArtifacts: RunbookArtifact[]
  subtaskStats: { total: number; completed: number } | null | undefined
  collectorSources: CollectorSourceCardData[]
  showCollectorSourcesSection: boolean
  policySlug: string | undefined
  isApproved: boolean
  workspaceId: string
  testId: string
  statusId: string
  auditCycleId?: string
  /** When set, render frozen artifacts from a submission revision instead of
   *  live test data. Skips live queries (doc links, evidence packs, ticket
   *  attachments) and disables all mutation buttons (unlink, delete). */
  snapshot?: ArtifactsSnapshot
  /** Override the section heading. Defaults to "Artifacts" / "Subtasks" /
   *  "Sample Items" depending on test type. */
  sectionLabel?: string
  /** Skip the section heading entirely (no `<h3>` row). Use when the parent
   *  surface provides its own framing and a duplicate label adds noise. */
  hideSectionLabel?: boolean
  /** Drop the outer rounded card around the file list (border + bg). Use
   *  when the parent provides its own surface. */
  unstyled?: boolean
  /** Optional content rendered as the last row INSIDE the evidence card's
   *  bordered container (picks up the `divide-y` top seam automatically), so
   *  it reads as attached to the card rather than as a separate section
   *  below. Used for the AI evidence-review strip on the test detail page. */
  footerSlot?: ReactNode
  /** Optional row rendered between the section heading and the evidence card,
   *  so its actions stay above the evidence however long the list grows. Used
   *  for the test detail page's action bar. */
  toolbar?: ReactNode | ((bulkActions: ReactNode, downloadAction?: EvidenceBulkAction, removeAllAction?: EvidenceBulkAction) => ReactNode)
  /** Evidence preparation content rendered below the heading, before actions. */
  preparationSlot?: ReactNode
  /** Recommended evidence (the test page's RecommendedEvidenceSection). While
   *  the interactive empty state shows and `hasRecommendations` is true, it
   *  leads the card ("Ready to add") and the drop-zone shrinks to one line, so
   *  the answer sits where "No evidence yet" used to. Otherwise it renders
   *  between the heading and toolbar. Only this component knows which state it is in,
   *  hence the render prop. */
  recommendations?: (placement: RecommendedEvidencePlacement) => ReactNode
  hasRecommendations?: boolean
  /** Current automated evidence and coverage guidance, alongside attachments. */
  coverageGuidance?: ReactNode
  /** Empty-state shortcuts. When provided (utility mode, not read-only), the
   *  "No evidence yet" placeholder becomes an interactive dashed drop-zone:
   *  the upload icon triggers `onAttachFiles`, and each inline link in the
   *  hint sentence triggers its handler. Every link is optional — the
   *  sentence is composed from whichever handlers the page wired, so a
   *  shortcut that doesn't apply to this test simply isn't mentioned.
   *  Omitted in read-only/snapshot views, which fall back to the plain text
   *  placeholder. */
  onAttachFiles?: () => void
  onAttachFromVault?: () => void
  onCaptureInBrowser?: () => void
  onRunAutomatedCheck?: () => void
  /** Number of automated-check runs on this test, or 0/undefined when it is
   *  not an API check. An automated check's proof is its run history, not an
   *  upload — so when such a test has no artifacts, the tall "drag & drop a
   *  file" dropzone misreads as missing evidence on a test that is passing.
   *  Non-zero swaps that empty state for a compact explanatory row; attaching
   *  a file stays available for supplementary evidence. */
  automatedCheckRunCount?: number
  /** True while the queries that decide WHICH empty state to show are still
   *  on their first load. Both the file list and the run count start out
   *  `undefined`, which reads as "no artifacts, no runs" — so without this the
   *  tall dropzone renders for a frame and is then replaced by the automated-
   *  check summary (or by the file list). Suppresses the empty state until
   *  emptiness is actually known. */
  evidenceStateResolving?: boolean
  /** Browser-recording bundles that would previously have rendered a
   *  "Save as Procedure" action. Procedure Checks owns that action now. */
  onConvertibleBrowserRecordingsChange?: (
    recordings: ConvertibleBrowserRecording[]
  ) => void
}

export interface ConvertibleBrowserRecording {
  executionId: string
  flowId: string
}

export type ArtifactsSnapshot = SubmissionArtifactSnapshot

type FileEntry = {
  id: string
  filename: string
  url?: string
  ticketUrl?: string | null
  mimeType?: string
  size?: number
  uploadedAt?: Date | string
  uploadedBy?: string | null
  kind?: 'file' | 'api' | 'runbook' | 'linked' | 'ticket'
  meta?: string
  linkId?: string
  sourceUrl?: string
  metadata?: Record<string, unknown> | null
  /** Linked-doc preview hint: lets the View handler lazy-fetch markdown
   *  content for policies / content-only vault docs via
   *  `evidenceDocLink.getPreviewContent`. Absent for uploaded files. */
  previewDocType?: 'policy' | 'vault'
  previewDocId?: string
  previewVersionId?: string | null
}

type DocumentPreviewState = {
  id: string
  filename: string
  tiptapJson: unknown
  markdown: string
}

const RECORDING_SOURCE_TYPES = new Set([
  'browser_recording_summary',
  'browser_recording',
  'browser_recording_video',
  'evidence_pack_manifest',
  'url_capture',
  'url_capture_manifest',
  // Runbook execution evidence (unified evidence model) — grouped per
  // WorkflowExecution into one "Procedure run" bundle.
  'runbook_execution_manifest',
  'runbook_browser_screenshot',
  'runbook_step_upload',
  'runbook_check_evidence',
  'runbook_browser_recording',
])

function isApiRunFile(file: FileEntry): boolean {
  if (file.kind !== 'file') return false
  const m = (
    file.metadata && typeof file.metadata === 'object' ? file.metadata : {}
  ) as { sourceType?: unknown; apiRunId?: unknown }
  return m.sourceType === 'api_test_run' && typeof m.apiRunId === 'string'
}

/** A signed evidence-pack bundle. Browser recordings and API checks both
 *  surface as one of these so the artifact list renders them through the
 *  same row component instead of two parallel pipelines. */
interface BundleGroup {
  /** Stable React key. */
  key: string
  /** Drives icon + header copy. */
  bundleType:
    | 'browser_recording'
    | 'api_check'
    | 'submission'
    | 'url_capture'
    | 'runbook_execution'
    | 'access_review'
    | 'risk_assessment'
    | 'period_rollup'
  /** "Browser recording" / "AWS S3 public-access check" / etc. */
  title: string
  /** "by Tao Huang <tao@…>" */
  submitterLabel: string | null
  submittedAt: string | null
  /** Manifest signing fields — drive the green Signed badge. */
  signatureStatus: string | null
  signingKeyFingerprint: string | null
  /** Files inside the bundle, in display order. */
  files: FileEntry[]
  /** Browser-recording-only: ties the bundle to a WorkflowExecution so
   *  the group-level Delete mutation can target it. */
  executionId: string | null
  /** API-check-only: ties the bundle to an ApiTestRun so the group-level
   *  Delete mutation can target it. */
  apiRunId?: string | null
  /** API-check-only: pass/fail label for the header chip. */
  resultStatus?: 'PASS' | 'FAIL' | null
  /** Optional run code shown in the header (`RUN-ymbh` / `TEST-1434`). */
  runTag?: string | null
  /** Submission-only: ties the bundle to an EvidenceSubmission row. */
  submissionId?: string | null
  /** Submission-only: revision number from the submission row. */
  revisionNumber?: number | null
  /** Access-review-only: the review's periodKey, for the "open review" link. */
  accessReviewPeriodKey?: string | null
}

function fileMetadata(file: FileEntry): Record<string, unknown> {
  return (
    file.metadata && typeof file.metadata === 'object' ? file.metadata : {}
  ) as Record<string, unknown>
}

/** Signed access-review artifacts: on a Test they are a link to the pack. */
function isAccessReviewFile(file: FileEntry): boolean {
  const sourceType = fileMetadata(file).sourceType
  return sourceType === 'access_review' || sourceType === 'access_review_period'
}

function isRecordingFile(file: FileEntry): boolean {
  if (file.kind !== 'file') return false
  const m = fileMetadata(file)
  const sourceType = typeof m.sourceType === 'string' ? m.sourceType : null
  const executionId = typeof m.executionId === 'string' ? m.executionId : null
  return !!sourceType && !!executionId && RECORDING_SOURCE_TYPES.has(sourceType)
}

const TICKET_PROVIDER_LABEL: Record<string, string> = {
  jira: 'Jira',
  github: 'GitHub',
  linear: 'Linear',
}

function ticketProviderLabel(provider: string | null | undefined): string {
  if (!provider) return 'Ticket'
  return TICKET_PROVIDER_LABEL[provider.toLowerCase()] ?? provider
}

export function ArtifactsSection({
  emptyTitle,
  testType,
  filesData,
  latestRunbookArtifacts,
  subtaskStats,
  collectorSources,
  showCollectorSourcesSection,
  policySlug,
  isApproved,
  workspaceId,
  statusId,
  auditCycleId,
  snapshot,
  sectionLabel: sectionLabelOverride,
  hideSectionLabel = false,
  unstyled = false,
  footerSlot,
  toolbar,
  preparationSlot,
  recommendations,
  hasRecommendations = false,
  coverageGuidance,
  onAttachFiles,
  onAttachFromVault,
  onCaptureInBrowser,
  onRunAutomatedCheck,
  automatedCheckRunCount = 0,
  evidenceStateResolving = false,
  onConvertibleBrowserRecordingsChange,
}: ArtifactsSectionProps) {
  // Snapshot mode locks mutations and reads from caller-provided data.
  // `isApproved` here is the broader "locked" signal — true for both
  // APPROVED and SUBMITTED statuses, since a submission's signed manifest
  // covers the EvidenceFile list at submit time. Mutating files after
  // submit would invalidate that manifest.
  const isSnapshot = !!snapshot
  const readOnly = isApproved || isSnapshot

  const docLinksQuery = useQuery({
    ...trpc.evidenceDocLink.list.queryOptions({ statusId }),
    // Older snapshots retain policy IDs but not their navigation slugs.
    enabled: !!statusId && (!isSnapshot || snapshot.evidenceDocLinks.some(d => d.documentType === 'policy' && !d.policySlug)),
  })
  const historicalFilesQuery = useQuery({
    ...trpc.evidenceFile.list.queryOptions({ statusId, includeHistorical: true }),
    enabled: !!statusId && isSnapshot && !!snapshot.files?.some(file => !file.metadata),
  })
  const evidencePacksQuery = useQuery({
    ...trpc.evidencePack.listForStatus.queryOptions({ statusId }),
    enabled: !!statusId && !isSnapshot,
  })
  const ticketAttachmentsQuery = useQuery({
    ...trpc.evidenceFile.listTicketAttachments.queryOptions({ statusId }),
    enabled: !!statusId && !isSnapshot,
  })
  const queryClient = useQueryClient()
  const invalidateArtifacts = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.evidenceDocLink.list.queryKey({ statusId }),
    })
    // Keep the Recommended-evidence section in sync — unlinking the policy
    // here flips its card back to "Add as evidence".
    queryClient.invalidateQueries({
      queryKey: trpc.compliance.getRecommendedEvidence.queryKey({ statusId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.evidenceFile.list.queryKey({ statusId }),
    })
    // A generated report prepares its key document; removing it brings the
    // "Suggested for this test" row back.
    queryClient.invalidateQueries({
      queryKey: trpc.cesTool.resolve.queryKey({ statusId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.evidencePack.listForStatus.queryKey({ statusId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.evidenceFile.listTicketAttachments.queryKey({ statusId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.audit.getActivityFeed.infiniteQueryKey({
        targetType: 'control_evidence_status',
        targetId: statusId,
      }),
    })
    // Evidence changed — mark any existing AI evidence review stale so the
    // card surfaces the "re-run" prompt immediately (recomputes stale flag,
    // does NOT trigger a new review).
    queryClient.invalidateQueries({
      queryKey: trpc.guidedTemplate.getEvidenceReview.queryKey({ statusId }),
    })
  }
  const unlinkMutation = useMutation(
    trpc.evidenceDocLink.unlink.mutationOptions({
      onSuccess: () => {
        invalidateArtifacts()
        queryClient.invalidateQueries({
          queryKey: trpc.evidenceDocLink.listLinkableDocuments.queryKey(),
        })
      },
    })
  )
  const deleteFileMutation = useMutation(
    trpc.evidenceFile.delete.mutationOptions({ onSuccess: invalidateArtifacts })
  )
  // Section-level actions: download every file as one zip, or clear the
  // evidence — signed submission artifacts move to history, access-review
  // artifacts are unlinked, other files are deleted, and every user-managed doc
  // link is removed. Program-backed deliverables are submission-managed and
  // never carry a linkId, so they stay.
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false)
  const [downloadingAll, setDownloadingAll] = useState(false)
  const deleteAllMutation = useMutation({
    mutationFn: async ({
      linkIds,
      hasFiles,
    }: {
      linkIds: string[]
      hasFiles: boolean
    }) => {
      const files = hasFiles
        ? await trpcClient.evidenceFile.deleteAllForStatus.mutate({ statusId })
        : { deleted: 0, unlinked: 0, archived: 0 }
      for (const linkId of linkIds) {
        await trpcClient.evidenceDocLink.unlink.mutate({ linkId })
      }
      return { ...files, docsUnlinked: linkIds.length }
    },
    onSuccess: async (result) => {
      setConfirmDeleteAll(false)
      toast.success(
        [
          result.deleted
            ? `${result.deleted} file${result.deleted === 1 ? '' : 's'} deleted`
            : '',
          result.unlinked
            ? `${result.unlinked} access-review artifact${result.unlinked === 1 ? '' : 's'} removed from evidence`
            : '',
          result.archived
            ? `${result.archived} artifact${result.archived === 1 ? '' : 's'} moved to history`
            : '',
          result.docsUnlinked
            ? `${result.docsUnlinked} linked document${result.docsUnlinked === 1 ? '' : 's'} unlinked`
            : '',
        ]
          .filter(Boolean)
          .join(' · ')
      )
      await invalidateArtifacts()
      queryClient.invalidateQueries({
        queryKey: trpc.evidenceDocLink.listLinkableDocuments.queryKey(),
      })
    },
    onError: (error: Error) => toast.error(error.message),
  })
  const downloadAll = async () => {
    setDownloadingAll(true)
    try {
      saveZipResult(
        await trpcClient.evidenceFile.downloadAllForStatus.query({ statusId })
      )
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to download evidence'
      )
    } finally {
      setDownloadingAll(false)
    }
  }
  const deleteRecordingMutation = useMutation(
    trpc.recording.deleteRecording.mutationOptions({
      onSuccess: invalidateArtifacts,
    })
  )
  // Remove a review bundle from evidence — unlinks its rows, the signed
  // packs stay; "Include" on the Test's access-review panel re-links it.
  const removeAccessReviewMutation = useMutation(
    trpc.evidenceFile.deleteMany.mutationOptions({
      onSuccess: () => {
        toast.success('Evidence package removed from evidence')
        invalidateArtifacts()
        queryClient.invalidateQueries({
          queryKey: trpc.accessReview.periods.queryKey(),
        })
      },
      onError: (err: { message?: string }) =>
        toast.error(err?.message ?? "Couldn't remove the evidence package"),
    })
  )
  // Detach a procedure run's bundle from evidence (reversible — re-attach via
  // the run history drawer), mirroring api-check's "remove from evidence".
  const detachRunbookRunMutation = useMutation(
    trpc.runbook.detachRunbookRunEvidence.mutationOptions({
      onSuccess: () => {
        toast.success('Procedure run removed from evidence')
        invalidateArtifacts()
      },
      onError: (err: { message?: string }) =>
        toast.error(err?.message ?? "Couldn't remove the procedure run"),
    })
  )
  // The bundle row's "Remove from evidence" affordance reuses the same
  // upsert mutation that the run history drawer uses — different entry
  // points, identical reconciliation semantics.
  const upsertFromApiRunsMutation = useMutation(
    trpc.evidenceFile.upsertFromApiRuns.mutationOptions({
      onSuccess: (result: { added: number; removed: number }) => {
        const parts: string[] = []
        if (result.added) parts.push(`${result.added} added`)
        if (result.removed) parts.push(`${result.removed} removed`)
        toast.success(
          parts.length > 0
            ? `Evidence updated (${parts.join(', ')})`
            : 'Evidence unchanged'
        )
        invalidateArtifacts()
      },
      onError: (err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Unknown error'
        toast.error(`Couldn't update evidence: ${msg}`)
      },
    })
  )
  const deleteSubmissionMutation = useMutation(
    // @ts-expect-error - Known tRPC mutation type inference issue
    trpc.compliance.deleteSubmission.mutationOptions({
      onSuccess: () => {
        toast.success('Submission deleted')
        invalidateArtifacts()
      },
      onError: (err: { message?: string }) => {
        toast.error(
          `Couldn't delete submission: ${err?.message ?? 'unknown error'}`
        )
      },
    })
  )

  // Conversion eligibility stays colocated with the bundle classification that
  // previously rendered the action. Procedure Checks owns the actual action.
  const runbookFeatureEnabled = useFeatureFlag('runbook').enabled

  const [previewFile, setPreviewFile] = useState<FileEntry | null>(null)
  // A file opened by clicking an evidence link INSIDE a previewed markdown
  // (e.g. a procedure run-summary). Rendered as a second, stacked preview
  // dialog so closing it returns to the summary instead of closing everything.
  const [nestedPreviewFile, setNestedPreviewFile] = useState<FileEntry | null>(
    null,
  )
  // Separate state for inline document previews (TipTap/markdown content
  // for linked policies and content-only vault docs). Rendered through
  // `PolicyContentViewer` rather than `FilePreview` so the auditor sees
  // the same read-only experience as the policy detail page.
  const [previewDocument, setPreviewDocument] =
    useState<DocumentPreviewState | null>(null)

  const currentArtifactRunIds = useMemo(() => {
    if (!Array.isArray(filesData)) return [] as string[]
    const ids = new Set<string>()
    for (const file of filesData as Array<{
      metadata?: Record<string, unknown> | null
    }>) {
      const m = (file?.metadata ?? {}) as {
        sourceType?: unknown
        apiRunId?: unknown
      }
      if (m.sourceType === 'api_test_run' && typeof m.apiRunId === 'string') {
        ids.add(m.apiRunId)
      }
    }
    return Array.from(ids)
  }, [filesData])

  const canPreview = (file: FileEntry): boolean => {
    if (file.kind === 'ticket') return false
    if (file.kind === 'linked') {
      // Markdown preview is always available via the lazy fetch — except
      // for file-backed vault docs where we fall back to the presigned URL.
      if (file.previewDocType && file.previewDocId) return true
      if (file.url && file.mimeType) {
        return getFileType(file.mimeType, file.filename) !== 'other'
      }
      return false
    }
    if (!file.url) return false
    return getFileType(file.mimeType ?? '', file.filename) !== 'other'
  }

  /** Open the inline preview. Uploaded files and file-backed linked vault
   *  docs route to `FilePreview` (PDF/image/etc renderers). Linked
   *  policies and content-only vault docs lazy-fetch TipTap/markdown
   *  content and render through `PolicyContentViewer` — same read-only
   *  experience as the policy detail page, no app navigation. */
  const openPreview = async (file: FileEntry) => {
    const metadata = fileMetadata(file)
    const isAssessmentBody = metadata.sourceType === 'risk_assessment' && metadata.artifactKind === 'assessment_body'
    if (isAssessmentBody && typeof metadata.documentId === 'string' && typeof metadata.versionId === 'string') {
      file = { ...file, kind: 'linked', url: undefined, previewDocType: 'vault', previewDocId: metadata.documentId, previewVersionId: metadata.versionId }
    }
    const isFileBackedLinked =
      file.kind === 'linked' && !!file.url && !!file.mimeType
    if (file.kind !== 'linked' || isFileBackedLinked) {
      setPreviewFile(file)
      return
    }
    if (!file.previewDocType || !file.previewDocId) {
      setPreviewFile(file)
      return
    }
    try {
      const res = await queryClient.fetchQuery(
        trpc.evidenceDocLink.getPreviewContent.queryOptions({
          documentType: file.previewDocType,
          documentId: file.previewDocId,
          versionId: file.previewVersionId ?? null,
        })
      )
      if (res.kind === 'file') {
        // Shouldn't happen for the linked-doc preview path — the row
        // already has a presigned URL in that case — but fall back
        // gracefully if the doc state changed since the list was fetched.
        toast.error('Preview not available for this document')
        return
      }
      setPreviewDocument({
        id: file.id,
        filename: res.filename,
        tiptapJson: res.tiptapJson,
        markdown: res.content ?? '',
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load preview'
      toast.error(msg)
    }
  }

  const linkedDocs: FileEntry[] = isSnapshot
    ? snapshot!.evidenceDocLinks.map((d) => {
        const isPolicy = d.documentType === 'policy'
        const isDeliverable = d.isProgramBackedDeliverable
        const linkedPolicy = docLinksQuery.data?.find(link => link.documentType === 'policy' && link.documentId === d.documentId)
        const policySlug = d.policySlug ?? (linkedPolicy?.documentType === 'policy' ? linkedPolicy.policySlug : undefined)
        return {
          id: d.id,
          sourceUrl: isPolicy
            ? policySlug ? `/workspaces/${workspaceId}${auditCycleId ? `/auditor-center/${auditCycleId}` : ''}/policies/${encodeURIComponent(policySlug)}` : undefined
            : !isDeliverable ? `/workspaces/${workspaceId}/documents/${encodeURIComponent(d.documentId)}` : undefined,
          // No linkId in snapshot mode — unlink is a live-state mutation.
          filename: `${d.documentTitle || 'Untitled'}${d.version ? ` v${d.version}` : ''}`,
          // File-backed vault docs and generated deliverables render from
          // their frozen URL. Policies and content-only vault docs use the
          // lazy markdown fetch below.
          url: d.downloadUrl ?? undefined,
          mimeType: d.mimeType ?? undefined,
          kind: 'linked' as const,
          meta: isPolicy
            ? d.isApproved ? 'Approved policy' : 'Policy'
            : isDeliverable
              ? d.isApproved ? 'Approved generated document' : 'Generated document'
              : 'Document',
          previewDocType: isPolicy
            ? ('policy' as const)
            : isDeliverable
              ? undefined
              : ('vault' as const),
          previewDocId: isDeliverable ? undefined : d.documentId,
          previewVersionId: d.versionId ?? null,
        }
      })
    : (docLinksQuery.data ?? [])
        .filter((d: any) => !d.deleted)
        .map((d: any) => {
          const isPolicy = d.documentType === 'policy'
          const isDeliverable = d.isProgramBackedDeliverable === true
          const version = isDeliverable
            ? (d.systemDescriptionVersion ?? null)
            : (d.pinnedVersion ?? d.currentVersion)
          const versionLabel = version ? ` v${version}` : ''
          // Policy + vault doc-links are user-managed — they show the unlink
          // button (via the `!readOnly && file.linkId` check at the render
          // site). Program-backed deliverables are submission-managed and
          // must not be unlinked manually, so they drop `linkId`.
          return {
            id: d.id,
            sourceUrl: isPolicy
              ? d.policySlug ? `/workspaces/${workspaceId}${auditCycleId ? `/auditor-center/${auditCycleId}` : ''}/policies/${encodeURIComponent(d.policySlug)}` : undefined
              : !isDeliverable ? `/workspaces/${workspaceId}/documents/${encodeURIComponent(d.documentId)}` : undefined,
            linkId: isDeliverable ? undefined : d.id,
            filename: `${d.documentTitle || 'Untitled'}${versionLabel}`,
            // File-backed vault docs preview directly via presigned URL.
            // Everything else previews inline through the markdown fetch
            // path below — no in-app redirect.
            url: isPolicy ? undefined : (d.downloadUrl ?? undefined),
            mimeType: d.mimeType ?? undefined,
            kind: 'linked' as const,
            meta: isPolicy
              ? d.isApproved
                ? 'Approved policy'
                : 'Policy'
              : isDeliverable
                ? d.isApproved
                  ? 'Approved generated document'
                  : 'Generated document'
                : 'Document',
            // Program-backed deliverables omit previewDocType — `documentId` is the
            // ComplianceProgram id, not a VaultDocument id, so the
            // generic `getPreviewContent` lookup would 404. With this
            // field undefined, `openPreview` falls back to the
            // file-backed branch and renders the .docx through
            // FilePreview/DocxPreview using the presigned URL.
            previewDocType: isPolicy
              ? ('policy' as const)
              : isDeliverable
                ? undefined
                : ('vault' as const),
            previewDocId: isDeliverable ? undefined : d.documentId,
            previewVersionId: d.versionId ?? null,
          }
        })

  const artifactFiles = isSnapshot
    ? restoreSnapshotFileMetadata(snapshot.files, Array.isArray(historicalFilesQuery.data) ? historicalFilesQuery.data : undefined)
    : filesData
  const uploadedFiles = useMemo<FileEntry[]>(
    () =>
      Array.isArray(artifactFiles)
        ? (artifactFiles as FileEntry[])
            .filter(
              (file) =>
                file && typeof file === 'object' && typeof file.id === 'string',
            )
            .map((file) => ({ ...file, kind: 'file' as const }))
        : [],
    [artifactFiles],
  )

  const ticketFiles: FileEntry[] = (ticketAttachmentsQuery.data ?? []).map(
    (link: any) => {
      const provider = ticketProviderLabel(link.provider)
      const ticketLabel = link.externalTicketId
        ? `${provider} ${link.externalTicketId}`
        : provider
      return {
        id: link.id,
        filename: link.filename,
        url: link.url ?? link.externalAttachmentUrl ?? undefined,
        ticketUrl: link.externalTicketUrl ?? null,
        mimeType: link.mimeType ?? undefined,
        size: link.size ?? undefined,
        uploadedAt: link.uploadedAt,
        kind: 'ticket' as const,
        meta: `${ticketLabel} attachment`,
      }
    }
  )

  const runbookFiles: FileEntry[] = latestRunbookArtifacts.map((a) => ({
    id: a.id,
    filename: a.filename,
    url: a.url,
    mimeType: a.mimeType,
    size: a.size,
    kind: 'runbook' as const,
  }))

  // Partition uploaded files into recording / API-run bundles vs. loose
  // files. Each recording's manifest, summary, and screenshots collapse
  // into one bundle keyed by `metadata.executionId`. API-run-derived
  // files (created by `evidenceFile.upsertFromApiRuns`) collapse into
  // one bundle per `metadata.apiRunId` so the artifact list shows
  // "AWS · RUN-alk8" once with its result/log/manifest nested
  // underneath, not three sibling rows.
  const { browserBundles, apiRunBundles, looseUploadedFiles } = useMemo(() => {
    const groupMap = new Map<
      string,
      {
        executionId: string
        bundleType: 'browser_recording' | 'url_capture' | 'runbook_execution'
        title: string
        submitterLabel: string | null
        submittedAt: string | null
        signatureStatus: string | null
        signingKeyFingerprint: string | null
        summary?: FileEntry
        manifest?: FileEntry
        screenshots: FileEntry[]
        rawFiles: FileEntry[]
      }
    >()
    const apiGroupMap = new Map<
      string,
      {
        apiRunId: string
        provider: string | null
        files: FileEntry[]
        latestUploadedAt: string | null
      }
    >()
    const loose: FileEntry[] = []
    const riskAssessmentMap = new Map<string, BundleGroup>()
    // One bundle per signed access review; its application packs sit in
    // folders (`metadata.folder`) under the period pack.
    const accessReviewMap = new Map<
      string,
      { key: string; title: string; periodKey: string | null; files: FileEntry[]; latestUploadedAt: string | null; signatureStatus: string | null }
    >()
    for (const file of uploadedFiles) {
      const metadata = fileMetadata(file)
      if (metadata.sourceType === 'risk_assessment' && typeof metadata.evidencePackId === 'string') {
        let group = riskAssessmentMap.get(metadata.evidencePackId)
        if (!group) {
          group = { key: metadata.evidencePackId, bundleType: 'risk_assessment',
            title: typeof metadata.label === 'string' ? metadata.label : 'Risk assessment',
            files: [], executionId: null, submitterLabel: null,
            submittedAt: file.uploadedAt ? new Date(file.uploadedAt).toISOString() : null,
            signatureStatus: typeof metadata.signatureStatus === 'string' ? metadata.signatureStatus : null,
            signingKeyFingerprint: null }
          riskAssessmentMap.set(metadata.evidencePackId, group)
        }
        group.files.push(file)
        continue
      }
      if (file.kind === 'file' && isAccessReviewFile(file)) {
        const m = fileMetadata(file)
        const key =
          (typeof m.accessReviewPeriodKey === 'string' && m.accessReviewPeriodKey) ||
          (typeof m.accessReviewCycleId === 'string' && `cycle:${m.accessReviewCycleId}`) ||
          'access-review'
        let group = accessReviewMap.get(key)
        if (!group) {
          group = {
            key,
            title:
              typeof m.accessReviewLabel === 'string' && m.accessReviewLabel
                ? m.accessReviewLabel
                : 'Access review',
            periodKey:
              typeof m.accessReviewPeriodKey === 'string' ? m.accessReviewPeriodKey : null,
            files: [],
            latestUploadedAt: null,
            signatureStatus: null,
          }
          accessReviewMap.set(key, group)
        }
        group.files.push(file)
        if (
          m.artifactKind === 'access_review_period_manifest' &&
          typeof m.signatureStatus === 'string'
        ) {
          group.signatureStatus = m.signatureStatus
        }
        const ts = file.uploadedAt
          ? typeof file.uploadedAt === 'string'
            ? file.uploadedAt
            : (file.uploadedAt.toISOString?.() ?? null)
          : null
        if (ts && (!group.latestUploadedAt || ts > group.latestUploadedAt)) {
          group.latestUploadedAt = ts
        }
        continue
      }
      if (isApiRunFile(file)) {
        const m = fileMetadata(file)
        const apiRunId = String(m.apiRunId)
        const provider =
          typeof m.provider === 'string' && m.provider ? m.provider : null
        let group = apiGroupMap.get(apiRunId)
        if (!group) {
          group = {
            apiRunId,
            provider,
            files: [],
            latestUploadedAt: null,
          }
          apiGroupMap.set(apiRunId, group)
        }
        group.files.push(file)
        const ts = file.uploadedAt
          ? typeof file.uploadedAt === 'string'
            ? file.uploadedAt
            : (file.uploadedAt.toISOString?.() ?? null)
          : null
        if (ts && (!group.latestUploadedAt || ts > group.latestUploadedAt)) {
          group.latestUploadedAt = ts
        }
        continue
      }
      if (!isRecordingFile(file)) {
        loose.push(file)
        continue
      }
      const m = fileMetadata(file)
      const executionId = String(m.executionId)
      let group = groupMap.get(executionId)
      if (!group) {
        group = {
          executionId,
          bundleType: 'browser_recording',
          title: 'Browser recording',
          submitterLabel: null,
          submittedAt: null,
          signatureStatus: null,
          signingKeyFingerprint: null,
          screenshots: [],
          rawFiles: [],
        }
        groupMap.set(executionId, group)
      }
      group.rawFiles.push(file)
      if (m.sourceType === 'url_capture' || m.sourceType === 'url_capture_manifest') {
        group.bundleType = 'url_capture'
        group.title =
          typeof m.sourceUrl === 'string' && m.sourceUrl.trim()
            ? m.sourceUrl
            : 'URL screenshot'
      }
      if (m.sourceType === 'url_capture_manifest') {
        group.manifest = file
        if (typeof m.signatureStatus === 'string')
          group.signatureStatus = m.signatureStatus
        if (typeof m.signingKeyFingerprint === 'string')
          group.signingKeyFingerprint = m.signingKeyFingerprint
      } else if (m.sourceType === 'evidence_pack_manifest') {
        group.manifest = file
        const submitterName =
          typeof m.submitterName === 'string' ? m.submitterName : null
        const submitterEmail =
          typeof m.submitterEmail === 'string' ? m.submitterEmail : null
        group.submitterLabel =
          submitterName && submitterEmail
            ? `${submitterName} <${submitterEmail}>`
            : submitterName || submitterEmail || null
        if (typeof m.submittedAt === 'string') group.submittedAt = m.submittedAt
        if (typeof m.signatureStatus === 'string')
          group.signatureStatus = m.signatureStatus
        if (typeof m.signingKeyFingerprint === 'string')
          group.signingKeyFingerprint = m.signingKeyFingerprint
        if (typeof m.title === 'string' && m.title.trim()) group.title = m.title
      } else if (m.sourceType === 'runbook_execution_manifest') {
        // A "Procedure run" bundle's root files all share this sourceType;
        // `role` distinguishes them. Only `manifest` is the anchor (title +
        // signing chain). README.md renders as a root file.
        group.bundleType = 'runbook_execution'
        if (m.role === 'manifest') {
          group.manifest = file
          if (typeof m.title === 'string' && m.title.trim()) group.title = m.title
          if (typeof m.signatureStatus === 'string')
            group.signatureStatus = m.signatureStatus
          if (typeof m.signingKeyFingerprint === 'string')
            group.signingKeyFingerprint = m.signingKeyFingerprint
        } else {
          group.rawFiles.push(file)
        }
      } else if (m.sourceType === 'runbook_browser_screenshot') {
        group.bundleType = 'runbook_execution'
        group.screenshots.push(file)
      } else if (
        m.sourceType === 'runbook_step_upload' ||
        m.sourceType === 'runbook_check_evidence'
      ) {
        // Human uploads + check-step canonical JSON stay in rawFiles (rendered
        // after screenshots); just tag the bundle as a procedure run.
        group.bundleType = 'runbook_execution'
      } else if (m.sourceType === 'browser_recording_summary') {
        group.summary = file
        if (group.title === 'Browser recording') {
          if (typeof m.title === 'string' && m.title.trim())
            group.title = m.title
        }
        // Newer commits don't create a manifest row anymore — the summary
        // carries the submitter attribution instead. The manifest branch
        // above still wins for legacy bundles (don't overwrite).
        if (!group.submitterLabel) {
          const submitterName =
            typeof m.submitterName === 'string' ? m.submitterName : null
          const submitterEmail =
            typeof m.submitterEmail === 'string' ? m.submitterEmail : null
          group.submitterLabel =
            submitterName && submitterEmail
              ? `${submitterName} <${submitterEmail}>`
              : submitterName || submitterEmail || null
        }
        if (!group.submittedAt && typeof m.submittedAt === 'string')
          group.submittedAt = m.submittedAt
      } else if (
        m.sourceType === 'browser_recording' ||
        m.sourceType === 'url_capture'
      ) {
        group.screenshots.push(file)
      }
    }
    // Stable ordering inside each bundle: summary → manifest → screenshots.
    // Screenshots come from `prisma.evidenceFile.createMany`, which doesn't
    // guarantee read-back order, so sort by capturedAt (set from the
    // original Screenshot.timestamp at write time). Falls back to step
    // number parsed from the filename, then filename, so older rows
    // missing capturedAt still render in a sensible order.
    const bundles: BundleGroup[] = Array.from(groupMap.values()).map((g) => {
      const sortedScreenshots = [...g.screenshots].sort((a, b) => {
        const am = fileMetadata(a)
        const bm = fileMetadata(b)
        const at = typeof am.capturedAt === 'string' ? am.capturedAt : ''
        const bt = typeof bm.capturedAt === 'string' ? bm.capturedAt : ''
        if (at && bt && at !== bt) return at.localeCompare(bt)
        // Numeric step index — matches both the recording `_step-N` and the
        // runbook `step-N_…` filename forms; numeric parse so step-10 sorts
        // after step-2.
        const stepRe = /step-(\d+)/
        const ai = Number(
          a.filename.match(stepRe)?.[1] ?? Number.POSITIVE_INFINITY
        )
        const bi = Number(
          b.filename.match(stepRe)?.[1] ?? Number.POSITIVE_INFINITY
        )
        if (ai !== bi) return ai - bi
        return a.filename.localeCompare(b.filename)
      })
      const ordered: FileEntry[] = []
      if (g.summary) ordered.push(g.summary)
      if (g.manifest) ordered.push(g.manifest)
      ordered.push(...sortedScreenshots)
      for (const f of g.rawFiles) {
        if (!ordered.includes(f)) ordered.push(f)
      }
      return {
        key: `${g.bundleType}-${g.executionId}`,
        bundleType: g.bundleType,
        title: g.title,
        submitterLabel: g.submitterLabel,
        submittedAt: g.submittedAt,
        signatureStatus: g.signatureStatus,
        signingKeyFingerprint: g.signingKeyFingerprint,
        files: ordered,
        executionId: g.executionId,
      }
    })

    const apiBundles: BundleGroup[] = Array.from(apiGroupMap.values()).map(
      (g) => {
        // Stable ordering inside the bundle: result → log → manifest →
        // anything else with an unknown kind. Drives the rendered file list.
        const order = ['json', 'log', 'manifest']
        const ordered: FileEntry[] = [...g.files].sort((a, b) => {
          const ak =
            (a.metadata && typeof a.metadata === 'object'
              ? (a.metadata as { runFileKind?: string }).runFileKind
              : null) ?? ''
          const bk =
            (b.metadata && typeof b.metadata === 'object'
              ? (b.metadata as { runFileKind?: string }).runFileKind
              : null) ?? ''
          return order.indexOf(ak) - order.indexOf(bk)
        })
        const provider = g.provider?.toUpperCase() ?? 'API'
        const runTag = `RUN-${g.apiRunId.slice(-4)}`
        return {
          key: `api-run-${g.apiRunId}`,
          bundleType: 'api_check',
          title: `${provider} · ${runTag}`,
          submitterLabel: null,
          submittedAt: g.latestUploadedAt,
          signatureStatus: null,
          signingKeyFingerprint: null,
          files: ordered,
          executionId: null,
          apiRunId: g.apiRunId,
          runTag,
        }
      }
    )

    const accessReviewBundles: BundleGroup[] = Array.from(
      accessReviewMap.values()
    ).map((g) => ({
      key: `access-review-${g.key}`,
      bundleType: 'access_review',
      title: g.title,
      submitterLabel: null,
      submittedAt: g.latestUploadedAt,
      signatureStatus: g.signatureStatus,
      signingKeyFingerprint: null,
      // Period pack first (no folder), then application folders.
      files: [...g.files].sort((a, b) => {
        const af = folderOfBundleFile(a) ?? ''
        const bf = folderOfBundleFile(b) ?? ''
        return af.localeCompare(bf) || a.filename.localeCompare(b.filename)
      }),
      executionId: null,
      accessReviewPeriodKey: g.periodKey,
    }))

    return {
      browserBundles: [...bundles, ...accessReviewBundles, ...Array.from(riskAssessmentMap.values(), group => ({ ...group, files: [...group.files].sort((a, b) => ['assessment_body', 'risk_register', 'manifest'].indexOf(String(fileMetadata(a).artifactKind)) - ['assessment_body', 'risk_register', 'manifest'].indexOf(String(fileMetadata(b).artifactKind))) }))],
      apiRunBundles: apiBundles,
      looseUploadedFiles: loose,
    }
  }, [uploadedFiles])

  // Browser recordings + user-curated API runs render through the same
  // BundleGroupRow so the Artifacts list shows one collapsible card per
  // run (with its result/log/manifest nested), not flat sibling rows.
  const periodBundles = useMemo(() => {
    const groups = new Map<string, BundleGroup>()
    for (const file of looseUploadedFiles) {
      const metadata = fileMetadata(file)
      if (!['anchor_rollup_summary', 'anchor_rollup_runs', 'procedure_rollup_summary', 'procedure_rollup_runs'].includes(String(metadata.artifactKind))) continue
      const rollup = metadata.periodRollup as { evidenceFromDate?: string; evidenceToDate?: string } | undefined
      const period = (metadata.period ?? { fromDate: rollup?.evidenceFromDate, toDate: rollup?.evidenceToDate }) as { fromDate?: string; toDate?: string }
      if (!period?.fromDate || !period.toDate) continue
      const key = `period:${metadata.originStatusId ?? statusId}:${period.fromDate}:${period.toDate}`
      let group = groups.get(key)
      if (!group) {
        group = {
          key, bundleType: 'period_rollup',
          title: `Period rollup · ${period.fromDate.slice(0, 10)} to ${period.toDate.slice(0, 10)}`,
          submittedAt: file.uploadedAt instanceof Date ? file.uploadedAt.toISOString() : file.uploadedAt ?? null, submitterLabel: null,
          signatureStatus: null, signingKeyFingerprint: null, executionId: null, files: [],
        }
        groups.set(key, group)
      }
      group.files.push(file)
    }
    return [...groups.values()]
  }, [looseUploadedFiles, statusId])
  const allBundles = useMemo(() => {
    return [...browserBundles, ...apiRunBundles, ...periodBundles].sort((a, b) => {
      const aT = a.submittedAt ? Date.parse(a.submittedAt) : 0
      const bT = b.submittedAt ? Date.parse(b.submittedAt) : 0
      return bT - aT
    })
  }, [browserBundles, apiRunBundles, periodBundles])

  const convertibleBrowserRecordings = useMemo(
    () =>
      runbookFeatureEnabled && !readOnly
        ? allBundles.flatMap((bundle) => {
            const flowId = bundle.files
              .map(fileMetadata)
              .map((metadata) => metadata.flowId)
              .find((value): value is string => typeof value === 'string')
            return bundle.bundleType === 'browser_recording' &&
              bundle.executionId &&
              flowId
              ? [{ executionId: bundle.executionId, flowId }]
              : []
          })
        : [],
    [allBundles, readOnly, runbookFeatureEnabled]
  )

  useEffect(() => {
    onConvertibleBrowserRecordingsChange?.(convertibleBrowserRecordings)
  }, [convertibleBrowserRecordings, onConvertibleBrowserRecordingsChange])

  useEffect(
    () => () => onConvertibleBrowserRecordingsChange?.([]),
    [onConvertibleBrowserRecordingsChange]
  )

  const allFiles = [
    ...looseUploadedFiles.filter(file => !periodBundles.some(group => group.files.includes(file))),
    ...ticketFiles,
    ...runbookFiles,
    ...linkedDocs,
  ]
  const totalArtifactCount =
    allFiles.length + allBundles.reduce((acc, g) => acc + g.files.length, 0)

  // An evidence-file link inside a previewed markdown (e.g. a procedure
  // run-summary) → resolve the storage href back to its FileEntry and swap the
  // preview to it (in-app preview), instead of opening the raw URL.
  const openEvidenceLink = (href: string) => {
    const match = [
      ...allFiles,
      ...allBundles.flatMap((g) => g.files),
    ].find((f) => f.url === href)
    // Open in a stacked dialog so closing it returns to the run-summary
    // preview (rather than swapping/closing the summary).
    if (match) setNestedPreviewFile(match)
    else window.open(href, '_blank', 'noopener,noreferrer')
  }

  // Map a FileEntry to the shape EvidenceFilePreviewDialog expects (null when
  // there's no file to preview).
  const toPreviewFile = (file: FileEntry | null) =>
    file?.url
      ? {
          evidenceFileId: file.kind === 'file' ? file.id : undefined,
          url: file.url,
          filename: file.filename,
          mimeType: file.mimeType ?? null,
        }
      : null

  const hasSubtasks =
    testType === 'CONTAINER' && subtaskStats && subtaskStats.total > 0
  const hasCollectors =
    showCollectorSourcesSection && collectorSources.length > 0

  const uploadedFileCount = uploadedFiles.filter(
    (file) => file.kind === 'file'
  ).length
  const accessReviewFileCount = uploadedFiles.filter(
    (file) => file.kind === 'file' && isAccessReviewFile(file)
  ).length
  // User-managed doc links (policies / vault docs). Deliverables have no linkId.
  const userLinkIds = linkedDocs.flatMap((d) => (d.linkId ? [d.linkId] : []))
  const canDeleteAll =
    !readOnly && uploadedFileCount + userLinkIds.length > 0

  const sectionLabel =
    sectionLabelOverride ??
    (testType === 'CONTAINER'
      ? 'Subtasks'
      : testType === 'SAMPLING'
        ? 'Sample Items'
        : 'Evidence')

  // Interactive dashed drop-zone empty state — only in utility mode (callbacks
  // wired) with nothing else to show. The dashed box supplies its own border,
  // so the solid card chrome is dropped to avoid a double border.
  // "Empty" is only meaningful once the underlying queries have settled;
  // mid-load they report undefined, which looks identical to empty.
  const isEmpty =
    !coverageGuidance &&
    !evidenceStateResolving &&
    totalArtifactCount === 0 &&
    !hasSubtasks &&
    !hasCollectors
  // An automated check with runs has evidence — it just isn't an uploaded
  // artifact. Prompting for a file there states the opposite of the truth.
  const showAutomatedCheckEmpty = isEmpty && automatedCheckRunCount > 0
  const showSupplementalUpload =
    !!coverageGuidance && !readOnly && !!onAttachFiles && !evidenceStateResolving &&
    totalArtifactCount === 0 && !hasSubtasks && !hasCollectors
  const showInteractiveEmpty =
    (isEmpty || showSupplementalUpload) && !!onAttachFiles && !showAutomatedCheckEmpty
  // Inline links inside the empty-state hint. Each entry appears only when
  // the page wired its handler, so the sentence never offers a route this
  // test can't take (e.g. an automated check on a test that already has one).
  const emptyStateShortcuts = [
    onAttachFromVault && {
      label: 'attach existing documents in vault',
      onClick: onAttachFromVault,
    },
    onCaptureInBrowser && {
      label: 'capture in browser',
      onClick: onCaptureInBrowser,
    },
    onRunAutomatedCheck && {
      label: 'set up API automation',
      onClick: onRunAutomatedCheck,
    },
  ].filter(Boolean) as { label: string; onClick: () => void }[]
  const emptyStateShortcutLinks = (
    <>
      {emptyStateShortcuts.map((shortcut, index) => (
        <Fragment key={shortcut.label}>
          {index === emptyStateShortcuts.length - 1 ? ', or ' : ', '}
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation()
              shortcut.onClick()
            }}
            className='cursor-pointer font-medium text-cobalt-strong hover:text-primary hover:underline'
          >
            {shortcut.label}
          </button>
        </Fragment>
      ))}{' '}
      to generate evidence.
    </>
  )
  const leadWithRecommendations =
    showInteractiveEmpty && !coverageGuidance && hasRecommendations && !!recommendations

  const bulkActions = !isSnapshot && (uploadedFileCount > 0 || canDeleteAll) ? (
    <div className='flex items-center gap-1'>
      {uploadedFileCount > 0 && typeof toolbar !== 'function' ? (
        <button
          type='button'
          className='flex items-center gap-1 rounded-sm px-1 py-1 text-[11px] text-text-subtle transition-colors hover:text-text-secondary disabled:opacity-50'
          disabled={downloadingAll}
          onClick={downloadAll}
        >
          {downloadingAll ? (
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
          ) : (
            <Download className='h-3.5 w-3.5' />
          )}
          Download all
        </button>
      ) : null}
      {canDeleteAll && typeof toolbar !== 'function' ? (
        <button
          type='button'
          className='flex items-center gap-1 rounded-sm px-1 py-1 text-[11px] text-text-subtle transition-colors hover:text-red-500 disabled:opacity-50'
          disabled={deleteAllMutation.isPending}
          onClick={() => setConfirmDeleteAll(true)}
        >
          <Trash2 className='h-3.5 w-3.5' />
          Remove all
        </button>
      ) : null}
    </div>
  ) : null
  const renderedToolbar = typeof toolbar === 'function'
    ? toolbar(bulkActions, !isSnapshot && uploadedFileCount > 0
      ? { onClick: downloadAll, disabled: downloadingAll } : undefined,
      canDeleteAll ? { onClick: () => setConfirmDeleteAll(true), disabled: deleteAllMutation.isPending } : undefined)
    : toolbar

  return (
    <div className={hideSectionLabel ? '' : 'mb-4'}>
      {hideSectionLabel ? null : (
        <div className='mb-2 flex flex-wrap items-center justify-between gap-3'>
          <h3 className='text-[10px] font-medium tracking-wider text-primary uppercase'>
            {sectionLabel}
            {totalArtifactCount > 0 && (
              <span className='ml-1.5 text-text-disabled'>
                ({totalArtifactCount})
              </span>
            )}
          </h3>
          {isSnapshot && renderedToolbar && !coverageGuidance ? (
            <div className='ml-auto'>{renderedToolbar}</div>
          ) : null}
          {typeof toolbar !== 'function' && bulkActions}
        </div>
      )}
      {/* Held while emptiness is unknown, so recommendations do not jump
          between the preparation area and the empty state while loading. */}
      {recommendations && !leadWithRecommendations && !evidenceStateResolving && (
        <div className='mb-4 empty:mb-0'>{recommendations('below')}</div>
      )}
      {coverageGuidance && <div className='mb-4 overflow-hidden rounded-sm border border-border bg-card'>{coverageGuidance}</div>}
      {preparationSlot}
      {renderedToolbar && (coverageGuidance || !isSnapshot || hideSectionLabel) ? <div className='mb-3'>{renderedToolbar}</div> : null}
      <AlertDialog open={confirmDeleteAll} onOpenChange={setConfirmDeleteAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove all evidence?</AlertDialogTitle>
            <AlertDialogDescription>
              {uploadedFileCount > 0
                ? `Removes ${uploadedFileCount} file${uploadedFileCount === 1 ? '' : 's'} from this test's current evidence. Signed submission artifacts are retained in history. `
                : ''}
              {userLinkIds.length > 0
                ? `Unlinks ${userLinkIds.length} linked document${userLinkIds.length === 1 ? '' : 's'}. ${userLinkIds.length === 1 ? 'It stays' : 'They stay'} in your library and can be linked again. `
                : ''}
              {accessReviewFileCount > 0
                ? `${accessReviewFileCount} signed access-review artifact${accessReviewFileCount === 1 ? '' : 's'} stay in their review pack and are only unlinked here. `
                : ''}
              {uploadedFileCount > 0 ? 'File deletion cannot be undone.' : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAllMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteAllMutation.isPending}
              onClick={(event) => {
                event.preventDefault()
                deleteAllMutation.mutate({
                  linkIds: userLinkIds,
                  hasFiles: uploadedFileCount > 0,
                })
              }}
            >
              {deleteAllMutation.isPending ? 'Removing…' : 'Remove all'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div
        className={
          unstyled
            ? 'divide-y divide-border'
            : showInteractiveEmpty
              ? '' // dashed empty-state button supplies its own border
              : 'divide-y divide-border overflow-hidden rounded-sm border border-border bg-card'
        }
      >
        {(isEmpty || showSupplementalUpload) &&
          (showAutomatedCheckEmpty ? (
            <AutomatedCheckEmptyState
              runCount={automatedCheckRunCount}
              onAttachFiles={onAttachFiles}
            />
          ) : showInteractiveEmpty ? (
            <>
              {leadWithRecommendations && (
                <div className='mb-4 rounded-sm border border-border bg-card px-4 pt-3 pb-4'>
                  {recommendations!('empty-state')}
                </div>
              )}
              {/* Clickable dashed drop-zone — matches AttachFilesPanel's own
                  zone (a div, not a button, so the inline Vault link can nest
                  without invalid interactive-in-button markup). */}
              <div
                // Coach-mark anchor (issue #1349): test-page tour evidence step.
                data-tour='test-evidence-dropzone'
                onClick={onAttachFiles}
                className='group flex cursor-pointer flex-col items-center justify-center gap-3 rounded-sm border-2 border-dashed border-border-divider bg-card px-4 py-10 text-center transition-colors hover:border-primary hover:bg-primary/5'
              >
                <span className='flex h-11 w-11 items-center justify-center rounded-md border border-border bg-card shadow-sm'>
                  <Upload className='h-[18px] w-[18px] text-text-subtle transition-colors group-hover:text-primary' />
                </span>
                <span className='text-[15px] font-medium text-text-secondary'>
                  {emptyTitle ?? 'No evidence yet'}
                </span>
                <span className='text-[12px] text-text-placeholder'>
                  Drag &amp; drop a file{emptyStateShortcutLinks}
                </span>
              </div>
            </>
          ) : (
            <div className='px-4 py-6 text-center text-[12px] text-text-placeholder'>
              No evidence yet. Attach files, link documents, or run a check to
              generate evidence.
            </div>
          ))}

        {allBundles.map((bundle) => (
          <BundleGroupRow
            key={bundle.key}
            bundle={bundle}
            isApproved={readOnly}
            deleteRecordingMutation={deleteRecordingMutation}
            onRemoveApiRunFromEvidence={(runId) =>
              upsertFromApiRunsMutation.mutate({
                statusId,
                runIds: currentArtifactRunIds.filter((id) => id !== runId),
              })
            }
            isRemoveApiRunFromEvidencePending={(runId) =>
              upsertFromApiRunsMutation.isPending &&
              !upsertFromApiRunsMutation.variables?.runIds?.includes(runId)
            }
            onRemoveRunbookRun={(executionId) =>
              detachRunbookRunMutation.mutate({ statusId, executionId })
            }
            onRemoveAccessReview={(ids) =>
              removeAccessReviewMutation.mutate({ ids })
            }
            isRemoveAccessReviewPending={(ids) =>
              removeAccessReviewMutation.isPending &&
              removeAccessReviewMutation.variables?.ids?.[0] === ids[0]
            }
            isRemoveRunbookRunPending={(executionId) =>
              detachRunbookRunMutation.isPending &&
              detachRunbookRunMutation.variables?.executionId === executionId
            }
            onDeleteSubmission={(submissionId) =>
              deleteSubmissionMutation.mutate({ submissionId })
            }
            isDeleteSubmissionPending={(submissionId) =>
              deleteSubmissionMutation.isPending &&
              deleteSubmissionMutation.variables?.submissionId === submissionId
            }
            canPreview={canPreview}
            onPreview={openPreview}
          />
        ))}

        {allFiles.map((file) => (
          <div key={file.id} className='flex items-center gap-3 px-4 py-2.5'>
            {file.kind === 'linked' ? (
              <Link2 className='h-4 w-4 text-text-subtle' />
            ) : file.kind === 'ticket' ? (
              <ExternalLink className='h-4 w-4 text-text-subtle' />
            ) : (
              <FileExtensionIcon
                filename={file.filename}
                className='h-4 w-4 shrink-0'
              />
            )}
            <div className='min-w-0 flex-1'>
              {file.sourceUrl ? (
                <a
                  href={file.sourceUrl}
                  target='_blank'
                  rel='noreferrer'
                  title='Open original document or policy'
                  className='block truncate text-[12px] font-medium text-cobalt-strong underline-offset-2 hover:underline'
                >
                  {file.filename}
                </a>
              ) : (
                <span className='block truncate text-[12px] font-medium text-text-secondary'>
                  {file.filename}
                </span>
              )}
              {file.metadata?.storageAvailability === 'unavailable' && (
                <p role='status' className='text-status-orange text-[11px]'>
                  File unavailable. Verify access or re-upload it.
                </p>
              )}
              <div className='flex items-center gap-2 text-[11px] text-text-placeholder'>
                {(file.kind === 'linked' ||
                  file.kind === 'api' ||
                  file.kind === 'ticket') && (
                  <span>
                    {file.meta ||
                      (file.kind === 'linked'
                        ? 'Linked document'
                        : file.kind === 'ticket'
                          ? 'Ticket attachment'
                          : 'API artifact')}
                  </span>
                )}
                {file.kind === 'runbook' && <span>Procedure artifact</span>}
                {file.size != null && file.size > 0 && (
                  <span>{formatFileSize(file.size, '')}</span>
                )}
                {file.uploadedAt && (
                  <span>
                    {formatDistanceToNow(new Date(file.uploadedAt), {
                      addSuffix: true,
                    })}
                  </span>
                )}
              </div>
            </div>
            <div className='flex shrink-0 items-center gap-1'>
              {canPreview(file) && (
                <button
                  type='button'
                  className='p-1 text-text-subtle transition-colors hover:text-cobalt-strong'
                  title={file.kind === 'linked' ? 'View' : 'Preview'}
                  onClick={() => {
                    void openPreview(file)
                  }}
                >
                  <Eye className='h-3.5 w-3.5' />
                </button>
              )}
              {file.kind === 'file' && fileMetadata(file).sourceType !== 'risk_assessment' && file.url && getFileType(file.mimeType ?? '', file.filename) === 'markdown' ? (
                <EvidencePdfDownload evidenceFileId={file.id} url={file.url} />
              ) : file.url ? (
                <a
                  href={file.url}
                  target='_blank'
                  rel='noreferrer'
                  className='p-1 text-text-subtle transition-colors hover:text-cobalt-strong'
                  title='Download'
                >
                  <Download className='h-3.5 w-3.5' />
                </a>
              ) : null}
              {file.ticketUrl && (
                <a
                  href={file.ticketUrl}
                  target='_blank'
                  rel='noreferrer'
                  className='p-1 text-text-subtle transition-colors hover:text-cobalt-strong'
                  title='Open ticket'
                >
                  <ExternalLink className='h-3.5 w-3.5' />
                </a>
              )}
              {file.kind === 'linked' && file.sourceUrl ? (
                <a
                  href={file.sourceUrl}
                  target='_blank'
                  rel='noreferrer'
                  aria-label={`Open original ${file.filename}`}
                  title='Open original document or policy'
                  className='p-1 text-status-green transition-colors hover:text-cobalt-strong'
                >
                  <Link2 className='h-3.5 w-3.5' aria-hidden='true' />
                </a>
              ) : isSnapshot && file.kind === 'linked' && (
                <span
                  role='img'
                  aria-label='Linked'
                  title='Linked to submitted evidence'
                  className='p-1 text-status-green'
                >
                  <Link2 className='h-3.5 w-3.5' aria-hidden='true' />
                </span>
              )}
              {!readOnly && file.linkId && (
                <button
                  className='p-1 text-text-subtle transition-colors hover:text-red-500 disabled:opacity-50'
                  title='Unlink'
                  disabled={unlinkMutation.isPending}
                  onClick={() =>
                    unlinkMutation.mutate({ linkId: file.linkId! })
                  }
                >
                  {unlinkMutation.isPending &&
                  unlinkMutation.variables?.linkId === file.linkId ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Unlink className='h-3.5 w-3.5' />
                  )}
                </button>
              )}
              {!readOnly && file.kind === 'file' && (
                <button
                  className='p-1 text-text-subtle transition-colors hover:text-red-500 disabled:opacity-50'
                  title={
                    isAccessReviewFile(file)
                      ? 'Remove from evidence (the signed review pack keeps this file)'
                      : 'Delete'
                  }
                  disabled={deleteFileMutation.isPending}
                  onClick={() => deleteFileMutation.mutate({ id: file.id })}
                >
                  {deleteFileMutation.isPending &&
                  deleteFileMutation.variables?.id === file.id ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Trash2 className='h-3.5 w-3.5' />
                  )}
                </button>
              )}
            </div>
          </div>
        ))}

        {hasSubtasks && subtaskStats && (
          <div className='flex items-center gap-3 px-4 py-3'>
            <ListChecks className='h-4 w-4 text-text-subtle' />
            <div className='flex-1'>
              <span className='text-[12px] font-medium text-text-secondary'>
                {subtaskStats.completed} of {subtaskStats.total} subtasks
                completed
              </span>
            </div>
            <Badge
              variant='outline'
              className={cn(
                'text-[10px]',
                subtaskStats.completed === subtaskStats.total
                  ? 'border-status-green/30 bg-status-green-bg text-status-green-strong'
                  : 'border-border'
              )}
            >
              {Math.round((subtaskStats.completed / subtaskStats.total) * 100)}%
            </Badge>
          </div>
        )}

        {hasCollectors &&
          collectorSources.map((source) => (
            <div
              key={source.sourceKey}
              className='flex items-center gap-3 px-4 py-2.5'
            >
              <Database className='h-4 w-4 text-text-subtle' />
              <div className='min-w-0 flex-1'>
                <span className='block truncate text-[12px] font-medium text-text-secondary'>
                  {source.displayName}
                </span>
                <div className='flex items-center gap-2 text-[11px] text-text-placeholder'>
                  <span>{source.provider}</span>
                  {source.rowCount > 0 && (
                    <span>
                      {source.rowCount} row{source.rowCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              <Badge
                variant='outline'
                className={cn(
                  'text-[10px]',
                  source.status === 'completed'
                    ? 'border-status-green/30 bg-status-green-bg text-status-green-strong'
                    : source.status === 'error'
                      ? 'border-status-red/30 bg-status-red-bg text-status-red-strong'
                      : 'border-border'
                )}
              >
                {source.status}
              </Badge>
            </div>
          ))}

        {/* Attached footer row (e.g. AI evidence review). Rendered inside the
            card so the `divide-y` gives it a top seam and it reads as part of
            the evidence card, not a separate section. */}
        {footerSlot}
      </div>

      <EvidenceFilePreviewDialog
        file={toPreviewFile(previewFile)}
        onClose={() => setPreviewFile(null)}
        onEvidenceFileClick={openEvidenceLink}
      />

      {/* Stacked preview for a file opened from a link inside the run-summary
          markdown — closing it returns to the summary above. */}
      <EvidenceFilePreviewDialog
        file={toPreviewFile(nestedPreviewFile)}
        onClose={() => setNestedPreviewFile(null)}
      />

      <Dialog
        open={previewDocument !== null}
        onOpenChange={(open) => !open && setPreviewDocument(null)}
      >
        <DialogContent
          className='flex h-[85vh] !max-w-[min(1100px,95vw)] flex-col gap-0 overflow-hidden rounded-md p-0'
          closeClassName='right-6 top-4'
        >
          <DialogHeader className='min-h-12 justify-center px-6 py-0'>
            <DialogTitle className='truncate pr-8 text-[13px] font-medium text-text-secondary'>
              {previewDocument?.filename}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea
            className='min-h-0 flex-1 bg-card'
            viewClassName='[&>div]:!block px-6 py-5'
          >
            {previewDocument && (
              <PolicyContentViewer
                contentKey={`linked-doc-preview-${previewDocument.id}`}
                plateContent={previewDocument.tiptapJson}
                contentMarkdown={previewDocument.markdown}
                readOnly
              />
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface BundleGroupRowProps {
  bundle: BundleGroup
  isApproved: boolean
  /** Whole-bundle delete: drops the EvidencePack, manifest, summary,
   *  per-screenshot EvidenceFiles, and WorkflowExecution as one
   *  transaction. Per-file delete is intentionally not exposed for
   *  bundled files because removing one of them would leave the manifest's
   *  hash list pointing at a missing URL. */
  deleteRecordingMutation: ReturnType<typeof useMutation<any, any, any, any>>
  /** Remove an API run's files from the evidence list. The run itself
   *  is preserved (still visible in the bar / drawer); the user can
   *  re-add it. Run-level deletion lives in the drawer. */
  onRemoveApiRunFromEvidence?: (runId: string) => void
  isRemoveApiRunFromEvidencePending?: (runId: string) => boolean
  /** Detach a procedure run's bundle from evidence (reversible — re-attach via
   *  the run history drawer). Only wired for `runbook_execution` bundles. */
  onRemoveRunbookRun?: (executionId: string) => void
  isRemoveRunbookRunPending?: (executionId: string) => boolean
  /** Unlink an access review's bundle from evidence (reversible — re-include
   *  from the Test's access-review panel). */
  onRemoveAccessReview?: (fileIds: string[]) => void
  isRemoveAccessReviewPending?: (fileIds: string[]) => boolean
  /** Delete an evidence submission while it's still in PENDING review.
   *  Drops narrative.md + manifest.json (row and R2 object) and reverts
   *  the included run files back to per-run bundles. */
  onDeleteSubmission?: (submissionId: string) => void
  isDeleteSubmissionPending?: (submissionId: string) => boolean
  canPreview: (file: FileEntry) => boolean
  onPreview: (file: FileEntry) => void
}

function BundleGroupRow({
  bundle,
  isApproved,
  deleteRecordingMutation,
  onRemoveApiRunFromEvidence,
  isRemoveApiRunFromEvidencePending,
  onRemoveRunbookRun,
  isRemoveRunbookRunPending,
  onRemoveAccessReview,
  isRemoveAccessReviewPending,
  onDeleteSubmission,
  isDeleteSubmissionPending,
  canPreview,
  onPreview,
}: BundleGroupRowProps) {
  const [expanded, setExpanded] = useState(true)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const isSigned =
    bundle.signatureStatus === 'SIGNED' ||
    bundle.signatureStatus === 'TSA_VERIFIED' ||
    bundle.signatureStatus === 'TSA_PENDING'
  const submittedDate = bundle.submittedAt ? new Date(bundle.submittedAt) : null
  const submittedDistance =
    submittedDate && !Number.isNaN(submittedDate.getTime())
      ? formatDistanceToNow(submittedDate, { addSuffix: true })
      : null

  const isApiCheck = bundle.bundleType === 'api_check'
  const isSubmission = bundle.bundleType === 'submission'
  const isUrlCapture = bundle.bundleType === 'url_capture'
  const isRunbook = bundle.bundleType === 'runbook_execution'
  const isRiskAssessment = bundle.bundleType === 'risk_assessment'
  const isAccessReview = bundle.bundleType === 'access_review'
  const isNativePack = isAccessReview || isRiskAssessment
  const isPeriodRollup = bundle.bundleType === 'period_rollup'
  const bundleFileIds = bundle.files.map((file) => file.id)
  // Browser recordings support whole-bundle delete (drops the
  // WorkflowExecution + manifest). API check bundles support "remove
  // from evidence" (deletes the EvidenceFile rows but keeps the run).
  // Submission bundles support delete while still PENDING review —
  // drops narrative + manifest and reverts the included runs.
  const supportsDelete =
    (bundle.bundleType === 'browser_recording' && !!bundle.executionId) ||
    (isApiCheck && !!bundle.apiRunId && !!onRemoveApiRunFromEvidence) ||
    (isRunbook && !!bundle.executionId && !!onRemoveRunbookRun) ||
    (isNativePack && !!onRemoveAccessReview) ||
    (isSubmission && !!bundle.submissionId && !!onDeleteSubmission)
  const deletePending = isApiCheck
    ? !!(
        bundle.apiRunId && isRemoveApiRunFromEvidencePending?.(bundle.apiRunId)
      )
    : isRunbook
    ? !!(bundle.executionId && isRemoveRunbookRunPending?.(bundle.executionId))
    : isNativePack
      ? !!isRemoveAccessReviewPending?.(bundleFileIds)
      : isSubmission
      ? !!(
          bundle.submissionId &&
          isDeleteSubmissionPending?.(bundle.submissionId)
        )
      : deleteRecordingMutation.isPending &&
        deleteRecordingMutation.variables?.executionId === bundle.executionId
  const screenshotCount =
    bundle.bundleType === 'browser_recording' || isUrlCapture || isRunbook
      ? bundle.files.filter((f) => {
          const m = (f.metadata ?? {}) as Record<string, unknown>
          return (
            m.sourceType === 'browser_recording' ||
            m.sourceType === 'url_capture' ||
            m.sourceType === 'runbook_browser_screenshot'
          )
        }).length
      : 0

  // The clickable header expand-toggle and the action buttons share a row,
  // so we render them as a flex `<div>` instead of nesting buttons in a
  // button — clicking Delete shouldn't also toggle expand.
  return (
    <div data-evidence-pack-id={isRiskAssessment ? bundle.key : undefined}>
      <div className='flex items-center gap-3 px-4 py-2.5 hover:bg-card'>
        <button
          type='button'
          onClick={() => setExpanded((v) => !v)}
          className='flex min-w-0 flex-1 items-center gap-3 text-left'
          aria-label={expanded ? 'Collapse bundle' : 'Expand bundle'}
        >
          {expanded ? (
            <ChevronDown className='h-4 w-4 shrink-0 text-text-subtle' />
          ) : (
            <ChevronRight className='h-4 w-4 shrink-0 text-text-subtle' />
          )}
          {isApiCheck ? (
            <Cloud className='h-4 w-4 shrink-0 text-cobalt-strong' />
          ) : isSubmission ? (
            <Package className='h-4 w-4 shrink-0 text-cobalt-strong' />
          ) : isUrlCapture ? (
            <Globe className='h-4 w-4 shrink-0 text-cobalt-strong' />
          ) : isRunbook ? (
            <ListChecks className='h-4 w-4 shrink-0 text-cobalt-strong' />
          ) : isNativePack ? (
            <Folder className='h-4 w-4 shrink-0 text-cobalt-strong' />
          ) : (
            <Film className='h-4 w-4 shrink-0 text-cobalt-strong' />
          )}
          <div className='min-w-0 flex-1'>
            <span className='block truncate text-[12px] font-medium text-text-secondary'>
              {bundle.title}
            </span>
            <div className='flex flex-wrap items-center gap-2 text-[11px] text-text-placeholder'>
              {' '}
              {isApiCheck ? (
                <>
                  <span>Automated check</span>
                  {bundle.runTag && (
                    <>
                      <span>·</span>
                      <span>{bundle.runTag}</span>
                    </>
                  )}
                </>
              ) : isSubmission ? (
                <>
                  <span>Evidence submission</span>
                  <span>·</span>
                  <span>
                    {bundle.files.length} artifact
                    {bundle.files.length === 1 ? '' : 's'}
                  </span>
                </>
              ) : isRunbook ? (
                <>
                  <span>Procedure run</span>
                  <span>·</span>
                  <span>
                    {screenshotCount} screenshot
                    {screenshotCount === 1 ? '' : 's'}
                  </span>
                </>
              ) : isPeriodRollup ? (
                <span>Period evidence · {bundle.files.length} artifacts</span>
              ) : isNativePack ? (
                <>
                  <span>{isRiskAssessment ? 'Risk assessment evidence package' : 'Access review'}</span>
                  <span>·</span>
                  <span>
                    {bundle.files.length} artifact
                    {bundle.files.length === 1 ? '' : 's'}
                  </span>
                </>
              ) : (
                <>
                  <span>Browser recording</span>
                  <span>·</span>
                  <span>
                    {screenshotCount} screenshot
                    {screenshotCount === 1 ? '' : 's'}
                  </span>
                </>
              )}
              {bundle.submitterLabel && (
                <>
                  <span>·</span>
                  <span className='truncate'>by {bundle.submitterLabel}</span>
                </>
              )}
              {submittedDistance && (
                <>
                  <span>·</span>
                  <span>{submittedDistance}</span>
                </>
              )}
            </div>
          </div>
        </button>
        <div className='flex shrink-0 items-center gap-1'>
          {/* Pass/fail chip — only for API check bundles. */}
          {isApiCheck && bundle.resultStatus && (
            <Badge
              variant='outline'
              className={cn(
                'gap-1 text-[10px]',
                bundle.resultStatus === 'PASS'
                  ? 'border-status-green/30 bg-status-green-bg text-status-green-strong'
                  : 'border-status-red/30 bg-status-red-bg text-status-red-strong'
              )}
            >
              {bundle.resultStatus === 'PASS' ? 'Passed' : 'Failed'}
            </Badge>
          )}
          {/* Show the signed badge only when the manifest is actually
              signed — "Unsigned" on every dev/unconfigured bundle is just
              noise. The manifest itself still carries per-file sha256
              hashes for tamper-evidence; the badge is the additional
              "platform/customer key vouches for this list" signal. */}
          {isSigned && (
            <Badge
              variant='outline'
              className='gap-1 border-status-green/30 bg-status-green-bg text-[10px] text-status-green-strong'
              title={
                bundle.signingKeyFingerprint
                  ? `${bundle.signatureStatus} · key ${bundle.signingKeyFingerprint.slice(0, 12)}…`
                  : (bundle.signatureStatus ?? 'SIGNED')
              }
            >
              <ShieldCheck className='h-3 w-3' />
              Signed
            </Badge>
          )}
          {!isApproved && supportsDelete && (
            <button
              type='button'
              className='p-1 text-text-subtle transition-colors hover:text-red-500 disabled:opacity-50'
              title={
                isApiCheck || isRunbook || isNativePack
                  ? 'Remove from evidence'
                  : isSubmission
                    ? 'Delete submission'
                    : 'Delete recording'
              }
              disabled={deletePending}
              onClick={() => {
                // No confirm dialog for api-check / procedure runs: removing
                // from evidence is reversible (re-add via the run history drawer).
                if (isApiCheck && bundle.apiRunId) {
                  onRemoveApiRunFromEvidence?.(bundle.apiRunId)
                } else if (isRunbook && bundle.executionId) {
                  onRemoveRunbookRun?.(bundle.executionId)
                } else if (isNativePack) {
                  onRemoveAccessReview?.(bundleFileIds)
                } else {
                  setConfirmDeleteOpen(true)
                }
              }}
            >
              {deletePending ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : isApiCheck || isRunbook || isNativePack ? (
                <Unlink className='h-3.5 w-3.5' />
              ) : (
                <Trash2 className='h-3.5 w-3.5' />
              )}
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className='border-border border-t bg-card'>
          {(() => {
            const renderRow = (file: FileEntry, padClass: string) => (
              <div
                key={file.id}
                className={cn(
                  'border-border flex items-center gap-3 border-b px-4 py-2 last:border-b-0',
                  padClass,
                )}
              >
                <FileExtensionIcon filename={file.filename} className='h-4 w-4 shrink-0' />
                <div className='min-w-0 flex-1'>
                  <span className='block truncate text-[12px] font-medium text-text-secondary'>
                    {file.filename}
                  </span>
                  {file.metadata?.storageAvailability === 'unavailable' && (
                    <p role='status' className='text-status-orange text-[11px]'>
                      File unavailable. Verify access or re-upload it.
                    </p>
                  )}
                  <div className='flex items-center gap-2 text-[11px] text-text-placeholder'>
                    {file.size != null && file.size > 0 && (
                      <span>{formatFileSize(file.size, '')}</span>
                    )}
                    {file.uploadedAt && (
                      <span>
                        {formatDistanceToNow(new Date(file.uploadedAt), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                </div>
                <div className='flex shrink-0 items-center gap-1'>
                  {canPreview(file) && (
                    <button
                      type='button'
                      className='p-1 text-text-subtle transition-colors hover:text-cobalt-strong'
                      title='Preview'
                      onClick={() => onPreview(file)}
                    >
                      <Eye className='h-3.5 w-3.5' />
                    </button>
                  )}
                  {file.kind === 'file' && fileMetadata(file).sourceType !== 'risk_assessment' && file.url && getFileType(file.mimeType ?? '', file.filename) === 'markdown' ? (
                    <EvidencePdfDownload evidenceFileId={file.id} url={file.url} />
                  ) : file.url && (
                    <a
                      href={file.url}
                      target='_blank'
                      rel='noreferrer'
                      className='p-1 text-text-subtle transition-colors hover:text-cobalt-strong'
                      title='Download'
                    >
                      <Download className='h-3.5 w-3.5' />
                    </a>
                  )}
                  {/* No per-file delete inside a recording bundle — removing
                      one file would orphan it from the signed manifest's hash
                      list. Use the group-level Delete in the header instead. */}
                </div>
              </div>
            )

            // Runbook execution bundles carry a per-step `metadata.folder`
            // (`step-NN-<slug>`). Render root files (manifest.json / README.md)
            // then a section per step folder, sorted by `stepOrder`. Old bundles
            // (no folder) fall through to a flat list.
            if (
              !(isRunbook || isAccessReview) ||
              !bundle.files.some(folderOfBundleFile)
            ) {
              return bundle.files.map((file) => renderRow(file, 'pl-11'))
            }
            const { rootFiles, folders } = partitionRunbookBundleFiles(bundle.files)
            return (
              <>
                {rootFiles.map((file) => renderRow(file, 'pl-11'))}
                {folders.map(({ folder, files }) => (
                  <div key={folder}>
                    <div className='border-border flex items-center gap-2 border-b bg-surface-subtle px-4 py-1.5 pl-11 text-[11px] font-medium text-text-body'>
                      <Folder className='h-3.5 w-3.5 shrink-0' />
                      {folder}
                    </div>
                    {files.map((file) => renderRow(file, 'pl-16'))}
                  </div>
                ))}
              </>
            )
          })()}
        </div>
      )}

      {!isApiCheck && supportsDelete && (
        <AlertDialog
          open={confirmDeleteOpen}
          onOpenChange={setConfirmDeleteOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {isSubmission
                  ? 'Delete this submission?'
                  : 'Delete this recording?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {isSubmission
                  ? 'The narrative and signed manifest will be removed from the evidence pack and storage. The underlying API runs revert to per-run bundles and can be re-included in a new submission via the run history drawer.'
                  : 'The summary, screenshots, and signed manifest will all be removed. The underlying steps stay so you can re-edit later.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletePending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className='bg-red-600 text-white hover:bg-red-700 focus:ring-red-600'
                disabled={deletePending}
                onClick={() => {
                  if (isSubmission && bundle.submissionId) {
                    onDeleteSubmission?.(bundle.submissionId)
                  } else if (bundle.executionId) {
                    deleteRecordingMutation.mutate({
                      executionId: bundle.executionId,
                    })
                  }
                }}
              >
                {deletePending ? (
                  <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
                ) : null}
                {isSubmission ? 'Delete submission' : 'Delete recording'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}
